/**
 * 人員歷史記錄組件 | Person History Component
 * 顯示所有變更歷史記錄 | Display all change history records
 */

import { useState, useMemo, useCallback } from 'react';
import { HistoryEntry } from '@/types/history';
import { useResourceStore } from '@/store/resourceStore';
import { clearHistoryStorage } from '@/utils/localStorage';
import { toast } from '@/components/UI/Toast';

interface PersonHistoryProps {
  onBack: () => void;  // 返回列表視圖 | Back to list view
}

// 動作類型顯示名稱 | Action type display names
const ACTION_LABELS: Record<HistoryEntry['action'], string> = {
  create: '➕ 新增',
  update: '✏️ 修改',
  delete: '🗑️ 刪除',
};

// 動作類型顏色 | Action type colors
const ACTION_COLORS: Record<HistoryEntry['action'], string> = {
  create: 'bg-green-900/30 text-green-400',
  update: 'bg-blue-900/30 text-blue-400',
  delete: 'bg-red-900/30 text-red-400',
};

export function PersonHistory({ onBack }: PersonHistoryProps) {
  const { personHistory, clearPersonHistory, persons } = useResourceStore();

  // 過濾狀態 | Filter state
  const [filterAction, setFilterAction] = useState<'all' | HistoryEntry['action']>('all');
  const [filterEntityId, setFilterEntityId] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // 過濾後的歷史記錄 | Filtered history records
  const filteredHistory = useMemo(() => {
    return personHistory.filter((entry) => {
      // 動作過濾 | Action filter
      const matchesAction = filterAction === 'all' || entry.action === filterAction;

      // 實體過濾 | Entity filter
      const matchesEntity = filterEntityId === 'all' || entry.entityId === filterEntityId;

      // 搜尋過濾 | Search filter
      const matchesSearch =
        searchTerm === '' ||
        entry.entityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.entityId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.changes.some((change) =>
          change.fieldName.toLowerCase().includes(searchTerm.toLowerCase())
        );

      return matchesAction && matchesEntity && matchesSearch;
    });
  }, [personHistory, filterAction, filterEntityId, searchTerm]);

  // 清除歷史 | Clear history
  const handleClearHistory = useCallback(() => {
    clearPersonHistory();
    clearHistoryStorage();
    toast.success('已清除歷史記錄', '所有變更歷史已被清除', 3000);
  }, [clearPersonHistory]);

  // 格式化日期時間 | Format date time
  const formatDateTime = useCallback((isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }, []);

  // 格式化變更值 | Format change value
  const formatChangeValue = useCallback((value: any): string => {
    if (value === null) return '無';
    if (value === undefined) return '未定義';
    if (Array.isArray(value)) return value.join('、');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }, []);

  return (
    <div className="space-y-4">
      {/* 標題與操作列 | Header and action row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-bold text-[#0f172a]">📜 變更歷史</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onBack}
            className="px-4 py-2 bg-[#e2e8f0] hover:bg-[#cbd5e1] text-[#0f172a] rounded transition-colors text-sm font-medium"
          >
            ← 返回列表
          </button>
          <button
            onClick={handleClearHistory}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors text-sm font-medium"
          >
            🗑️ 清除歷史
          </button>
        </div>
      </div>

      {/* 統計資訊 | Statistics */}
      <div className="bg-white border border-[#e2e8f0] rounded-lg p-4 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-[#0f172a]">{personHistory.length}</div>
            <div className="text-sm text-[#64748b]">總記錄數</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-400">
              {personHistory.filter((e) => e.action === 'create').length}
            </div>
            <div className="text-sm text-[#94a3b8]">新增</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-400">
              {personHistory.filter((e) => e.action === 'update').length}
            </div>
            <div className="text-sm text-[#94a3b8]">修改</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-400">
              {personHistory.filter((e) => e.action === 'delete').length}
            </div>
            <div className="text-sm text-[#94a3b8]">刪除</div>
          </div>
        </div>
      </div>

      {/* 搜尋與過濾 | Search and filter */}
      <div className="bg-white border border-[#e2e8f0] rounded-lg p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 搜尋框 | Search */}
          <div>
            <label className="block text-sm font-medium text-[#475569] mb-1">
              🔍 搜尋
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜尋人員姓名、工號、欄位..."
              className="w-full px-3 py-2 bg-white border border-[#cbd5e1] rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc]"
            />
          </div>

          {/* 動作過濾 | Action filter */}
          <div>
            <label className="block text-sm font-medium text-[#475569] mb-1">
              動作類型
            </label>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value as any)}
              className="w-full px-3 py-2 bg-white border border-[#cbd5e1] rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc] transition-all duration-200 focus:ring-2 focus:ring-[#0066cc]/20"
            >
              <option value="all">全部</option>
              <option value="create">新增</option>
              <option value="update">修改</option>
              <option value="delete">刪除</option>
            </select>
          </div>

          {/* 人員過濾 | Person filter */}
          <div>
            <label className="block text-sm font-medium text-[#475569] mb-1">
              人員
            </label>
            <select
              value={filterEntityId}
              onChange={(e) => setFilterEntityId(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-[#cbd5e1] rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc] transition-all duration-200 focus:ring-2 focus:ring-[#0066cc]/20"
            >
              <option value="all">全部人員</option>
              {persons.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name} ({person.id})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 顯示資訊 | Display info */}
        <div className="mt-3 pt-3 border-t border-[#e2e8f0] text-sm text-[#64748b]">
          顯示 <strong className="text-[#0066cc]">{filteredHistory.length}</strong> 筆記錄
        </div>
      </div>

      {/* 歷史記錄列表 | History list */}
      {filteredHistory.length === 0 ? (
        <div className="bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <div className="text-6xl mb-4">📜</div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">
            {personHistory.length === 0 ? '暫無歷史記錄' : '沒有符合條件的記錄'}
          </h3>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            {personHistory.length === 0
              ? '人員資料的變更歷史會顯示在這裡，包括新增、修改和刪除操作'
              : '請嘗試調整搜尋關鍵字或過濾器條件'}
          </p>
          <div className="flex items-center justify-center gap-3">
            {searchTerm || filterAction !== 'all' || filterEntityId !== 'all' ? (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterAction('all');
                  setFilterEntityId('all');
                }}
                className="px-5 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-700 rounded-lg transition-all text-sm font-semibold shadow-sm active:scale-95"
              >
                清除過濾
              </button>
            ) : (
              <button
                onClick={onBack}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl active:scale-95"
              >
                返回人員列表
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredHistory
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
            .map((entry) => (
              <div
                key={entry.id}
                className="bg-white border border-[#e2e8f0] rounded-lg p-4 hover:border-[#cbd5e1] transition-colors shadow-sm"
              >
                {/* 標題列 | Header row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <div className="flex items-center gap-3">
                    {/* 動作標籤 | Action badge */}
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${ACTION_COLORS[entry.action]}`}
                    >
                      {ACTION_LABELS[entry.action]}
                    </span>
                    {/* 人員資訊 | Person info */}
                    <span className="text-sm font-medium text-[#0f172a]">
                      {entry.entityName} ({entry.entityId})
                    </span>
                  </div>
                  {/* 時間戳 | Timestamp */}
                  <span className="text-xs text-[#64748b]">
                    {formatDateTime(entry.timestamp)}
                  </span>
                </div>

                {/* 變更詳情 | Change details */}
                <div className="bg-[#f8fafc] rounded p-3 space-y-2">
                  {entry.action === 'delete' ? (
                    <div className="text-sm text-[#64748b]">
                      <span className="font-medium">已刪除資料：</span>
                      <pre className="mt-1 p-2 bg-white rounded overflow-x-auto text-xs border border-[#e2e8f0]">
                        {formatChangeValue(entry.changes[0]?.oldValue)}
                      </pre>
                    </div>
                  ) : entry.action === 'create' ? (
                    <div className="text-sm text-[#64748b]">
                      <span className="font-medium">已新增資料</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-[#475569]">變更欄位：</div>
                      {entry.changes.map((change, index) => (
                        <div key={index} className="text-sm">
                          <div className="text-[#475569] mb-1">• {change.fieldName}</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                            <div>
                              <span className="text-red-600">舊值：</span>{' '}
                              <span className="text-[#64748b]">{formatChangeValue(change.oldValue)}</span>
                            </div>
                            <div>
                              <span className="text-green-600">新值：</span>{' '}
                              <span className="text-[#64748b]">{formatChangeValue(change.newValue)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 變更原因 | Change reason */}
                  {entry.reason && (
                    <div className="mt-2 pt-2 border-t border-[#e2e8f0] text-sm">
                      <span className="text-[#475569]">原因：</span>
                      <span className="text-[#64748b]">{entry.reason}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
